/**
 * Design tokens for the redesigned list/table UI (the "assets requiring
 * attention" triage redesign, sc-35741, now being rolled out to other list
 * pages such as Devices).
 *
 * These hex values were previously copy-pasted at the top of every redesigned
 * component (AssetsTable, AppliedFilterBar, MenuSelect, MachinesPage, ...).
 * They are centralised here so every migrated page shares a single source of
 * truth. Import the named tokens instead of re-declaring local consts.
 */

import { css } from 'styled-components';

// Ink / text
export const INK = '#383838'; // primary text
export const INK_2 = '#6b6e66'; // secondary text
export const INK_3 = '#999b95'; // muted / placeholder / icons

// Lines & surfaces
export const LINE = '#e5e8e1'; // borders
export const LINE_2 = '#eff0ee'; // subtle row border / hover surface
export const BG = '#f6f7f4'; // page / table-header background

// Green accent (active / selected)
export const GREEN = '#6c8a2e';
export const GREEN_T = '#eef3e2'; // tint
export const GREEN_LINE = '#d6e3bb'; // border

// Blue accent (insights)
export const BLUE = '#2f6fd0';
export const BLUE_T = '#eaf1fb';
export const BLUE_LINE = '#cadcf2';

// Amber accent (caution: bug fixes, drafts)
export const AMBER = '#a15c07';
export const AMBER_T = '#fdf3e1';
export const AMBER_LINE = '#f3dcb0';

// Alert / destructive
export const CRITICAL = '#d92b2b';

// Scrollbar thumb: dark enough to find on white, light enough not to read as a
// divider next to LINE borders.
export const SCROLL_THUMB = '#dbdfd4';

// Radii used across pills, buttons, menus and tables
export const RADIUS = {
  button: '7px',
  menu: '8px',
  table: '10px',
  pill: '14px'
};

// Shared elevation for popovers / floating menus
export const POPOVER_SHADOW = '0 8px 28px rgba(40, 44, 36, 0.16)';

/**
 * Slim scrollbars for list-UI scroll containers.
 *
 * On any platform set to "always show scrollbars" (macOS with a mouse, most
 * Windows setups) the default bar is a thick, high-contrast slab that reads as
 * a stray divider slicing across a light surface — most obvious on the short
 * horizontal strips, where it is nearly as tall as the content it scrolls.
 *
 * The thumb keeps the full 10px track as its hit area but paints only the
 * middle 4px (transparent border + padding-box clip), so it stays grabbable
 * while reading as a hairline. Firefox gets the same via `scrollbar-width`.
 */
export const THIN_SCROLLBAR = css`
  scrollbar-width: thin;
  scrollbar-color: ${SCROLL_THUMB} transparent;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-track,
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${SCROLL_THUMB};
    background-clip: padding-box;
    border: 3px solid transparent;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${INK_3};
    background-clip: padding-box;
  }
`;

/**
 * Type scale for list rows.
 *
 * A page whose row is a plain element rather than DataTable's NameLink (Reports,
 * for one) still has to look identical to the pages that use it, so the sizes
 * live here rather than being re-typed per component.
 */
export const TYPE = {
  NAME_SIZE: '13px', // primary row identifier
  NAME_WEIGHT: 700,
  CELL_SIZE: '12.5px', // standard cell text and secondary lines
  HEADER_SIZE: '10.5px' // uppercase column headers
};

export default {
  INK,
  INK_2,
  INK_3,
  LINE,
  LINE_2,
  BG,
  GREEN,
  GREEN_T,
  GREEN_LINE,
  BLUE,
  BLUE_T,
  BLUE_LINE,
  AMBER,
  AMBER_T,
  AMBER_LINE,
  CRITICAL,
  SCROLL_THUMB,
  RADIUS,
  POPOVER_SHADOW,
  THIN_SCROLLBAR,
  TYPE
};
